import React, { useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import WebhookUrlPanel from "./listing/WebhookUrlPanel";
import { getCallCenterBaseUrl } from "../../service/CallCenterApi";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";

export const WHATSAPP_PROVIDERS = {
  META_DIRECT: "META_DIRECT",
  FLOWMATICAL: "FLOWMATICAL",
};

const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]";

export const emptyWhatsAppConfigValues = {
  enabled: false,
  provider: WHATSAPP_PROVIDERS.META_DIRECT,
  phoneNumberId: "",
  accessToken: "",
  webhookVerifyToken: "",
  appSecret: "",
  apiVersion: "v21.0",
  flowmaticalBaseUrl: "https://api.flowmatical.com",
  flowmaticalApiToken: "",
  flowmaticalWebhookSecret: "",
};

export const mapWhatsAppConfigResponse = (data) => ({
  values: {
    enabled: Boolean(data?.enabled),
    provider: data?.provider || WHATSAPP_PROVIDERS.META_DIRECT,
    phoneNumberId: data?.phoneNumberId || "",
    accessToken: "",
    webhookVerifyToken: "",
    appSecret: "",
    apiVersion: data?.apiVersion || "v21.0",
    flowmaticalBaseUrl: data?.flowmaticalBaseUrl || "https://api.flowmatical.com",
    flowmaticalApiToken: "",
    flowmaticalWebhookSecret: "",
  },
  flags: {
    accessTokenConfigured: Boolean(data?.accessTokenConfigured),
    webhookVerifyTokenConfigured: Boolean(data?.webhookVerifyTokenConfigured),
    appSecretConfigured: Boolean(data?.appSecretConfigured),
    flowmaticalApiTokenConfigured: Boolean(data?.flowmaticalApiTokenConfigured),
    flowmaticalWebhookSecretConfigured: Boolean(data?.flowmaticalWebhookSecretConfigured),
  },
  organizationId: data?.organizationId ?? null,
});

export const buildWhatsAppConfigPayload = (values) => {
  const payload = {
    enabled: Boolean(values.enabled),
    provider: values.provider,
    apiVersion: values.apiVersion.trim() || "v21.0",
  };

  if (values.provider === WHATSAPP_PROVIDERS.META_DIRECT) {
    payload.phoneNumberId = values.phoneNumberId.trim();
    if (values.accessToken.trim()) payload.accessToken = values.accessToken.trim();
    if (values.webhookVerifyToken.trim()) {
      payload.webhookVerifyToken = values.webhookVerifyToken.trim();
    }
    if (values.appSecret.trim()) payload.appSecret = values.appSecret.trim();
  } else {
    payload.flowmaticalBaseUrl = values.flowmaticalBaseUrl.trim();
    if (values.flowmaticalApiToken.trim()) {
      payload.flowmaticalApiToken = values.flowmaticalApiToken.trim();
    }
    if (values.flowmaticalWebhookSecret.trim()) {
      payload.flowmaticalWebhookSecret = values.flowmaticalWebhookSecret.trim();
    }
  }

  return payload;
};

export const buildWhatsAppConfigSchema = (t) =>
  Yup.object({
    enabled: Yup.boolean(),
    provider: Yup.string().oneOf([WHATSAPP_PROVIDERS.META_DIRECT, WHATSAPP_PROVIDERS.FLOWMATICAL]),
    phoneNumberId: Yup.string().trim().when(["enabled", "provider"], {
      is: (enabled, provider) => enabled && provider === WHATSAPP_PROVIDERS.META_DIRECT,
      then: (field) => field.required(t("ccWhatsAppPhoneNumberIdRequired")),
    }),
    accessToken: Yup.string().trim(),
    webhookVerifyToken: Yup.string().trim(),
    appSecret: Yup.string().trim(),
    apiVersion: Yup.string().trim().when(["enabled", "provider"], {
      is: (enabled, provider) => enabled && provider === WHATSAPP_PROVIDERS.META_DIRECT,
      then: (field) => field.required(t("ccWhatsAppApiVersionRequired")),
    }),
    flowmaticalBaseUrl: Yup.string().trim().when(["enabled", "provider"], {
      is: (enabled, provider) => enabled && provider === WHATSAPP_PROVIDERS.FLOWMATICAL,
      then: (field) => field.required(t("ccWhatsAppFlowmaticalBaseUrlRequired")),
    }),
    flowmaticalApiToken: Yup.string().trim(),
    flowmaticalWebhookSecret: Yup.string().trim(),
  });

const WhatsAppConfigForm = ({
  initialValues,
  flags,
  organizationId,
  saving = false,
  onSubmit,
  t,
  submitLabel,
}) => {
  const schema = useMemo(() => buildWhatsAppConfigSchema(t), [t]);
  const apiBase = useMemo(() => getCallCenterBaseUrl().replace(/\/$/, ""), []);
  const metaWebhookUrl = useMemo(() => `${apiBase}/api/whatsapp/webhook`, [apiBase]);
  const flowmaticalWebhookUrl = useMemo(() => {
    if (!organizationId) return "";
    return `${apiBase}/webhooks/flowmatical/${organizationId}`;
  }, [apiBase, organizationId]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, isSubmitting }) => (
        <Form className="space-y-5">
          <label className="flex items-center gap-3 text-sm font-medium text-[#111827]">
            <Field type="checkbox" name="enabled" className={CHECKBOX_CLASS} />
            {t("ccWhatsAppEnabled")}
          </label>

          <div>
            <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppProvider")}</label>
            <Field as="select" name="provider" className={FILTER_FIELD_CLASS}>
              <option value={WHATSAPP_PROVIDERS.META_DIRECT}>
                {t("ccWhatsAppProviderMetaDirect")}
              </option>
              <option value={WHATSAPP_PROVIDERS.FLOWMATICAL}>
                {t("ccWhatsAppProviderFlowmatical")}
              </option>
            </Field>
          </div>

          {values.provider === WHATSAPP_PROVIDERS.META_DIRECT ? (
            <>
              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppPhoneNumberId")}</label>
                <Field
                  name="phoneNumberId"
                  className={`${FILTER_FIELD_CLASS} ${
                    touched.phoneNumberId && errors.phoneNumberId ? "border-red-500" : ""
                  }`}
                  placeholder="123456789012345"
                />
                {touched.phoneNumberId && errors.phoneNumberId ? (
                  <p className="mt-1 text-xs text-red-600">{errors.phoneNumberId}</p>
                ) : null}
              </div>

              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppAccessToken")}</label>
                <Field
                  type="password"
                  name="accessToken"
                  className={FILTER_FIELD_CLASS}
                  placeholder={flags.accessTokenConfigured ? t("ccWhatsAppSecretConfiguredHint") : ""}
                />
              </div>

              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppWebhookVerifyToken")}</label>
                <Field
                  type="password"
                  name="webhookVerifyToken"
                  className={FILTER_FIELD_CLASS}
                  placeholder={
                    flags.webhookVerifyTokenConfigured ? t("ccWhatsAppSecretConfiguredHint") : ""
                  }
                />
              </div>

              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppAppSecret")}</label>
                <Field
                  type="password"
                  name="appSecret"
                  className={FILTER_FIELD_CLASS}
                  placeholder={flags.appSecretConfigured ? t("ccWhatsAppSecretConfiguredHint") : ""}
                />
              </div>

              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppApiVersion")}</label>
                <Field
                  name="apiVersion"
                  className={`${FILTER_FIELD_CLASS} ${
                    touched.apiVersion && errors.apiVersion ? "border-red-500" : ""
                  }`}
                />
                {touched.apiVersion && errors.apiVersion ? (
                  <p className="mt-1 text-xs text-red-600">{errors.apiVersion}</p>
                ) : null}
              </div>

              <WebhookUrlPanel
                label={t("ccWhatsAppMetaWebhookUrlLabel")}
                url={metaWebhookUrl}
                hint={t("ccWhatsAppMetaWebhookHint")}
                copyLabel={t("ccWhatsAppCopyUrl")}
                copiedLabel={t("ccWhatsAppUrlCopied")}
              />
            </>
          ) : (
            <>
              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppFlowmaticalBaseUrl")}</label>
                <Field
                  name="flowmaticalBaseUrl"
                  className={`${FILTER_FIELD_CLASS} ${
                    touched.flowmaticalBaseUrl && errors.flowmaticalBaseUrl ? "border-red-500" : ""
                  }`}
                  placeholder="https://api.flowmatical.com"
                />
                {touched.flowmaticalBaseUrl && errors.flowmaticalBaseUrl ? (
                  <p className="mt-1 text-xs text-red-600">{errors.flowmaticalBaseUrl}</p>
                ) : (
                  <p className="mt-1 text-xs text-slate-400">{t("ccWhatsAppFlowmaticalBaseUrlHint")}</p>
                )}
              </div>

              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppFlowmaticalApiToken")}</label>
                <Field
                  type="password"
                  name="flowmaticalApiToken"
                  className={FILTER_FIELD_CLASS}
                  placeholder={
                    flags.flowmaticalApiTokenConfigured
                      ? t("ccWhatsAppSecretConfiguredHint")
                      : "flw_..."
                  }
                />
                <p className="mt-1 text-xs text-slate-400">{t("ccWhatsAppFlowmaticalApiTokenHint")}</p>
              </div>

              <div>
                <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppFlowmaticalWebhookSecret")}</label>
                <Field
                  type="password"
                  name="flowmaticalWebhookSecret"
                  className={FILTER_FIELD_CLASS}
                  placeholder={
                    flags.flowmaticalWebhookSecretConfigured
                      ? t("ccWhatsAppSecretConfiguredHint")
                      : "whsec_..."
                  }
                />
              </div>

              <WebhookUrlPanel
                label={t("ccWhatsAppFlowmaticalWebhookUrlLabel")}
                url={flowmaticalWebhookUrl}
                hint={t("ccWhatsAppFlowmaticalWebhookHint")}
                copyLabel={t("ccWhatsAppCopyUrl")}
                copiedLabel={t("ccWhatsAppUrlCopied")}
              />
            </>
          )}

          <p className="text-xs leading-5 text-slate-400">{t("ccWhatsAppSettingsHint")}</p>

          <button
            type="submit"
            disabled={saving || isSubmitting}
            className={`${LISTING_PRIMARY_BUTTON_CLASS} !bg-emerald-600 hover:!bg-emerald-700 disabled:opacity-60`}
          >
            {saving || isSubmitting ? t("ccSaving") : submitLabel || t("ccWhatsAppSave")}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default WhatsAppConfigForm;
